/**
 * Central Proxy Diagnostics Page
 *
 * A debugging tool for testing the backend's Central proxy endpoints.
 * Tests connectivity from frontend → backend → Central.
 */

import { useState } from 'react';
import { fetchSessionWorkspaces, selectSessionWorkspace, SessionWorkspace } from '../lib/centralSession';

interface TestResult {
  label: string;
  ok: boolean;
  message: string;
  timestamp: string;
  durationMs: number;
}

export function CentralDiagnosticsPage() {
  const [results, setResults] = useState<TestResult[]>([]);
  const [workspaces, setWorkspaces] = useState<SessionWorkspace[]>([]);
  const [selectedWorkspaceId, setSelectedWorkspaceId] = useState<string>('');
  const [isLoading, setIsLoading] = useState<string | null>(null);

  const apiUrl = import.meta.env.VITE_API_URL;

  const addResult = (result: TestResult) => {
    setResults((prev) => [result, ...prev]);
  };

  const handleHealthCheck = async () => {
    setIsLoading('health');
    const start = performance.now();
    try {
      // Test backend health endpoint
      const baseUrl = apiUrl?.replace(/\/api\/v1$/, '') || '';
      const response = await fetch(`${baseUrl}/health`);
      const data = await response.json();
      addResult({
        label: 'Backend Health Check',
        ok: response.ok,
        message: response.ok ? `Backend healthy: ${JSON.stringify(data)}` : `Backend returned ${response.status}`,
        timestamp: new Date().toISOString(),
        durationMs: Math.round(performance.now() - start),
      });
    } catch (err) {
      addResult({
        label: 'Backend Health Check',
        ok: false,
        message: err instanceof Error ? err.message : 'Network error',
        timestamp: new Date().toISOString(),
        durationMs: Math.round(performance.now() - start),
      });
    }
    setIsLoading(null);
  };

  const handleListWorkspaces = async () => {
    setIsLoading('list');
    const start = performance.now();
    const result = await fetchSessionWorkspaces();
    addResult({
      label: 'List Workspaces (via proxy)',
      ok: result.hasSession,
      message: result.hasSession
        ? `Found ${result.workspaces.length} workspace(s)`
        : result.error || 'No session',
      timestamp: new Date().toISOString(),
      durationMs: Math.round(performance.now() - start),
    });

    if (result.workspaces.length > 0) {
      setWorkspaces(result.workspaces);
      if (!selectedWorkspaceId) {
        setSelectedWorkspaceId(result.workspaces[0].id);
      }
    }
    setIsLoading(null);
  };

  const handleSelectWorkspace = async () => {
    if (!selectedWorkspaceId) return;
    setIsLoading('select');
    const start = performance.now();
    const result = await selectSessionWorkspace(selectedWorkspaceId);
    addResult({
      label: `Select Workspace (${selectedWorkspaceId})`,
      ok: result.ok,
      message: result.ok ? 'Workspace selected successfully' : result.error || 'Failed',
      timestamp: new Date().toISOString(),
      durationMs: Math.round(performance.now() - start),
    });
    setIsLoading(null);
  };

  return (
    <div className="min-h-screen bg-gray-50 p-6">
      <div className="max-w-4xl mx-auto space-y-6">
        {/* Header */}
        <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
          <h1 className="text-2xl font-bold text-gray-900 mb-2">
            Central Proxy Diagnostics
          </h1>
          <p className="text-gray-600 text-sm">
            Test connectivity: Frontend → Backend → Central.
            All Central calls are proxied through the backend.
          </p>
        </div>

        {/* Environment */}
        <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
          <h2 className="text-lg font-semibold text-gray-900 mb-4">Environment</h2>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4 font-mono text-sm">
            <div>
              <span className="text-gray-500">window.location.origin:</span>
              <div className="text-gray-900 bg-gray-50 px-2 py-1 rounded mt-1">
                {window.location.origin}
              </div>
            </div>
            <div>
              <span className="text-gray-500">VITE_API_URL:</span>
              <div className={`px-2 py-1 rounded mt-1 ${apiUrl ? 'text-gray-900 bg-gray-50' : 'text-red-600 bg-red-50'}`}>
                {apiUrl || '(NOT CONFIGURED)'}
              </div>
            </div>
          </div>
        </div>

        {/* Actions */}
        <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
          <h2 className="text-lg font-semibold text-gray-900 mb-4">Tests</h2>

          <div className="space-y-4">
            <div className="flex items-center gap-4 p-4 bg-gray-50 rounded-md">
              <div className="flex-1">
                <div className="font-medium text-gray-900">1. Backend Health</div>
                <div className="text-sm text-gray-500">GET /health</div>
              </div>
              <button
                onClick={handleHealthCheck}
                disabled={isLoading !== null}
                className="px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700 disabled:opacity-50"
              >
                {isLoading === 'health' ? 'Testing...' : 'Test'}
              </button>
            </div>

            <div className="flex items-center gap-4 p-4 bg-gray-50 rounded-md">
              <div className="flex-1">
                <div className="font-medium text-gray-900">2. List Workspaces (proxy)</div>
                <div className="text-sm text-gray-500">GET /api/v1/central/session/workspaces</div>
              </div>
              <button
                onClick={handleListWorkspaces}
                disabled={isLoading !== null || !apiUrl}
                className="px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700 disabled:opacity-50"
              >
                {isLoading === 'list' ? 'Testing...' : 'Test'}
              </button>
            </div>

            <div className="flex items-center gap-4 p-4 bg-gray-50 rounded-md">
              <div className="flex-1">
                <div className="font-medium text-gray-900">3. Select Workspace (proxy)</div>
                <div className="text-sm text-gray-500">POST /api/v1/central/session/select-workspace</div>
                {workspaces.length > 0 && (
                  <select
                    value={selectedWorkspaceId}
                    onChange={(e) => setSelectedWorkspaceId(e.target.value)}
                    className="mt-2 block w-full max-w-md px-3 py-2 border border-gray-300 rounded-md text-sm"
                  >
                    {workspaces.map((ws) => (
                      <option key={ws.id} value={ws.id}>
                        {ws.name} ({ws.role}) - {ws.id}
                      </option>
                    ))}
                  </select>
                )}
              </div>
              <button
                onClick={handleSelectWorkspace}
                disabled={isLoading !== null || !apiUrl || !selectedWorkspaceId}
                className="px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700 disabled:opacity-50"
              >
                {isLoading === 'select' ? 'Testing...' : 'Test'}
              </button>
            </div>
          </div>

          <div className="mt-4 pt-4 border-t border-gray-200">
            <button
              onClick={() => { setResults([]); setWorkspaces([]); setSelectedWorkspaceId(''); }}
              className="px-4 py-2 text-gray-600 border border-gray-300 rounded-md hover:bg-gray-50"
            >
              Clear Results
            </button>
          </div>
        </div>

        {/* Results */}
        <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
          <h2 className="text-lg font-semibold text-gray-900 mb-4">
            Results ({results.length})
          </h2>

          {results.length === 0 ? (
            <div className="text-gray-400 text-sm italic">
              No tests run yet. Click a button above to test an endpoint.
            </div>
          ) : (
            <div className="space-y-3">
              {results.map((r, idx) => (
                <div
                  key={idx}
                  className={`p-4 rounded-md border ${r.ok ? 'border-green-200 bg-green-50' : 'border-red-200 bg-red-50'}`}
                >
                  <div className="flex items-center justify-between">
                    <span className="font-medium text-sm">
                      {r.ok ? '✓' : '✗'} {r.label}
                    </span>
                    <span className="text-xs text-gray-400">{r.durationMs}ms</span>
                  </div>
                  <div className={`text-sm mt-1 ${r.ok ? 'text-green-700' : 'text-red-700'}`}>
                    {r.message}
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
